import { type Ref } from "vue";
import { generateApolloClient } from "@/common/apolloClientUtil";
import {
  ListDeliveryTimeQuery,
  ListDeliveryTimeQueryVariables,
  ListDeliveryTimeDocument,
  DeliveryTimeListInput,
} from "@/generated/graphql";
import { ApolloQueryResult } from "@apollo/client/core";
import type {
  ListData,
  ListDeliveryTimeItem,
  AreaListItem,
} from "../types";
import { DELIVERY_ITEMS, DISPLAY_DAYS_SETTING_ITEMS } from "../constants";

interface UseDeliverySettingTimeListParams {
  deliveryTimeListInput: DeliveryTimeListInput;
  listDeliveryTime: Ref<ListDeliveryTimeItem[]>;
  listDataArray: Ref<ListData[]>;
  mappedListData: Ref<Map<string, ListData[]>>;
  areaListItems: Ref<AreaListItem[]>;
}

/**
 * 時間帯一覧データの取得・整形処理を提供する composable。
 *
 * @returns 画面ロジックで利用する状態・関数
 */
export const useDeliverySettingTimeList = ({
  deliveryTimeListInput,
  listDeliveryTime,
  listDataArray,
  mappedListData,
  areaListItems,
}: UseDeliverySettingTimeListParams) => {
  /**
   * 配達時間一覧処理
   */
  const queryDeliverytime = async (): Promise<void> => {
    // graphql接続
    const apolloClient = await generateApolloClient();
    // 配達時間一覧取得 [引数:input参照]
    let deliveryTime: ApolloQueryResult<ListDeliveryTimeQuery>;
    try {
      deliveryTime = await apolloClient.query<
        ListDeliveryTimeQuery,
        ListDeliveryTimeQueryVariables
      >({
        query: ListDeliveryTimeDocument,
        variables: {
          input: deliveryTimeListInput,
        },
        fetchPolicy: "no-cache",
      });
    } catch (err: unknown) {
      console.error(err);
      return;
    }

    if (deliveryTime.data.listDeliveryTime && deliveryTime.data.listDeliveryTime.length > 0) {
      listDeliveryTime.value = deliveryTime.data.listDeliveryTime;
      listDataArray.value = [];
      // 配達時間一覧作成
      let area_name = "";
      let deliveryStartTimeAry: string[] = [];
      let deliveryEndTimeAry: string[] = [];
      let delivery_id_label = "";
      listDeliveryTime.value.forEach((value) => {
        areaListItems.value.forEach((item) => {
          if (item && value && item.area_code === value.area_code) {
            area_name = item.area_name;
          }
        });
        if (value) {
          deliveryStartTimeAry = value.delivery_start_time.split(":");
          deliveryEndTimeAry = value.delivery_end_time.split(":");

          for (const deliveryidkey in DELIVERY_ITEMS) {
            if (DELIVERY_ITEMS[deliveryidkey].delivery_id === value.delivery_id) {
              delivery_id_label = DELIVERY_ITEMS[deliveryidkey].label;
              break;
            }
          }

          listDataArray.value.push({
            delivery_id: delivery_id_label,
            delivery_start_time:
              deliveryStartTimeAry[0].padStart(2, "0") +
              ":" +
              deliveryStartTimeAry[1].padStart(2, "0"),
            delivery_end_time:
              deliveryEndTimeAry[0].padStart(2, "0") +
              ":" +
              deliveryEndTimeAry[1].padStart(2, "0"),
            delivery_time:
              deliveryStartTimeAry[0].padStart(2, "0") +
              ":" +
              deliveryStartTimeAry[1].padStart(2, "0") +
              "~" +
              deliveryEndTimeAry[0].padStart(2, "0") +
              ":" +
              deliveryEndTimeAry[1].padStart(2, "0"),
            max_deliverable_unit: value.max_deliverable_unit,
            delivery_cap_mon: value.delivery_cap_mon,
            delivery_cap_tue: value.delivery_cap_tue,
            delivery_cap_wed: value.delivery_cap_wed,
            delivery_cap_thu: value.delivery_cap_thu,
            delivery_cap_fri: value.delivery_cap_fri,
            delivery_cap_sat: value.delivery_cap_sat,
            delivery_cap_sun: value.delivery_cap_sun,
            display_days_setting: String(value.display_days_setting),
            area_code: value.area_code,
            area_name,
            delivery_date: value.delivery_date,
            name: value.name,
            group_delivery_id: value.group_delivery_id,
            group_code: value.group_code,
          });

          listDataArray.value.forEach((row) => {
            DISPLAY_DAYS_SETTING_ITEMS.forEach((setting) => {
              if (String(setting.display_days_setting) === row.display_days_setting) {
                row.display_days_setting = setting.label;
              }
            });
          });
        }
      });

      // エリアをキーにした形に変換
      const arealist = new Map<string, string>();
      let tmpMap: Map<string, ListData[]> = new Map();
      areaListItems.value.forEach((item) => {
        if (item) {
          arealist.set(item.area_code, item.area_name);
        }
      });
      listDataArray.value.forEach((row) => {
        let tmpArray = mappedListData.value.get(row.area_code);
        if (!tmpArray) {
          tmpArray = [];
        }
        tmpArray.push(row);
        mappedListData.value.set(row.area_code, tmpArray);
      });
      tmpMap = mappedListData.value;
      mappedListData.value = new Map<string, ListData[]>();
      mappedListData.value = tmpMap;
    }
  };

  return {
    queryDeliverytime,
  };
};
